import type React from 'react';
import { Link } from 'react-router-dom';
import classNames from '../../../../../utils/classNames';
import type { MeasuringPointWithProject } from '../../types';
import { ProjectMeasuringPointActionsCell } from './ProjectMeasuringPointActionsCell';
import { ProjectMeasuringPointDeviceCell } from './ProjectMeasuringPointDeviceCell';
import { ProjectMeasuringPointStatusCell } from './ProjectMeasuringPointStatusCell';
import { MEASURING_POINT_CELL_CLASSES } from './cellClasses';
import { SwarmName } from '../SwarmName';
import { useProjectMeasuringPointRowInteractions } from '../../hooks/useProjectMeasuringPointRowInteractions';
import { buildProjectMeasuringPointRowViewData } from '../../utils/projectMeasuringPointRowViewData';

type Props = {
    measuringPoint: MeasuringPointWithProject;
} & (
    | { layout: 'standalone' }
    | {
          /** Rendered inside the project table, on the grid from `overviewGrid.ts`. */
          layout: 'projectGrid';
          isProjectArchived: boolean;
      }
);

export function ProjectMeasuringPointRow(props: Props): React.ReactElement {
    const { measuringPoint } = props;
    const isProjectGrid = props.layout === 'projectGrid';
    const isProjectArchived = isProjectGrid && props.isProjectArchived;
    const viewData = buildProjectMeasuringPointRowViewData(measuringPoint);
    const interactions = useProjectMeasuringPointRowInteractions(measuringPoint);
    const canOpenMap = !viewData.isArchived && !isProjectArchived;
    const settingsSourceLabel = viewData.usesCustomSettingsSource
        ? gettext('CUSTOM')
        : gettext('PROJECT');

    return (
        <tr
            data-measuring-point-id={viewData.id}
            className="border-b border-gray-200 bg-gray-50 transition hover:bg-gray-100"
        >
            {isProjectGrid && <td className={MEASURING_POINT_CELL_CLASSES} />}
            <td className={MEASURING_POINT_CELL_CLASSES}>
                <SwarmName
                    name={viewData.name}
                    iconClass={viewData.swarmIconClass}
                    dimIcon={viewData.isDetachedDevice}
                />
            </td>
            <td className={classNames(MEASURING_POINT_CELL_CLASSES, 'text-gray-600')}>
                {canOpenMap ? (
                    <Link to={`/map/?measuringPointIds=${viewData.id}`}>{viewData.cityLabel}</Link>
                ) : (
                    <span>{viewData.cityLabel}</span>
                )}
            </td>
            <ProjectMeasuringPointStatusCell
                viewData={viewData}
                onRestart={interactions.onStatusRestart}
            />
            <ProjectMeasuringPointDeviceCell viewData={viewData} />
            <td className={classNames(MEASURING_POINT_CELL_CLASSES, 'text-gray-600')}>
                <span title={viewData.lastContactTitle}>{viewData.lastContactRelative}</span>
            </td>
            {isProjectGrid && (
                <td className={MEASURING_POINT_CELL_CLASSES}>
                    <span
                        className={classNames(
                            'inline-flex max-w-full items-center rounded-full px-2 py-0.5',
                            viewData.usesCustomSettingsSource
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-gray-100 text-gray-700'
                        )}
                        title={settingsSourceLabel}
                    >
                        <span className="block min-w-0 truncate">{settingsSourceLabel}</span>
                    </span>
                </td>
            )}
            <ProjectMeasuringPointActionsCell
                calibrationSkeletonCount={interactions.calibrationSkeletonCount}
                id={viewData.id}
                isArchived={viewData.isArchived}
                menuItems={interactions.menuItems}
                onOpenChange={interactions.onOptionsMenuOpenChange}
            />
            {isProjectGrid && <td />}
        </tr>
    );
}
